import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';
import {
    esInstitucionIps,
    resolverCodigoPrestadorFactura,
} from '../../../utils/codigoPrestadorSalud';
import { FORMA_PAGO_OPCIONES } from '../../../utils/formasPago';

function ModalDatosFacturacion({ show, onClose, onConfirm, profesionalId, datosRips, filtroPrefijoRelajado = false, presupuestosActivos = [], presupuestoSeleccionado, onSeleccionPresupuesto, saldoDisponible = 0, parametrosfevIdGuardado = null }) {
    const [datosFacturacion, setDatosFacturacion] = useState({
        prefijo: '',
        numerofactura: '',
        metodo_pago: '10',
        forma_pago: 'contado',
        id_concepto_factura: '',
        contract_number: '',
        policy_number: ''
    });

    // Estado para valores de transacción editables
    const [valoresTransaccion, setValoresTransaccion] = useState({
        valorconsulta: '0',
        descuento_consulta: '0',
        ajuste_consulta: '0',
        impuesto_consulta: '0',
        total_consulta: '0',
        valorprocedimiento: '0',
        descuento_procedimiento: '0',
        ajuste_procedimiento: '0',
        impuesto_procedimiento: '0',
        total_procedimiento: '0',
        total_bruto: '0',
        total_ajuste: '0',
        total_pagar: '0'
    });

    const [parametrosFev, setParametrosFev] = useState([]);
    const [conceptosFacturacion, setConceptosFacturacion] = useState([]);
    const [loading, setLoading] = useState(false);
    const [client, setClient] = useState(null);

    const esIps = esInstitucionIps(client);
    const codigoPrestadorSalud = useMemo(
        () => resolverCodigoPrestadorFactura(client, profesionalId, []),
        [client, profesionalId]
    );

    // Cargar parámetros FEV cuando se abre el modal
    // IMPORTANTE: Siempre recargar al abrir para obtener el consecutivo actualizado
    useEffect(() => {
        if (show && (profesionalId || parametrosfevIdGuardado)) {
            // Limpiar estado anterior para forzar recarga fresca
            setParametrosFev([]);
            setDatosFacturacion(prev => ({
                ...prev,
                prefijo: '',
                numerofactura: '',
                id_concepto_factura: ''
            }));
            cargarParametrosFev();
            cargarCliente();
            cargarConceptosFacturacion();
        }
    }, [show, profesionalId, parametrosfevIdGuardado]);

    // Inicializar valores de transacción cuando se reciben datosRips y recalcular totales
    useEffect(() => {
        if (show && datosRips) {
            const iniciales = {
                valorconsulta: datosRips.valorconsulta || '0',
                descuento_consulta: datosRips.descuento_consulta || '0',
                ajuste_consulta: datosRips.ajuste_consulta || '0',
                impuesto_consulta: datosRips.impuesto_consulta || '0',
                total_consulta: datosRips.total_consulta || '0',
                valorprocedimiento: datosRips.valorprocedimiento || '0',
                descuento_procedimiento: datosRips.descuento_procedimiento || '0',
                ajuste_procedimiento: datosRips.ajuste_procedimiento || '0',
                impuesto_procedimiento: datosRips.impuesto_procedimiento || '0',
                total_procedimiento: datosRips.total_procedimiento || '0',
                total_bruto: datosRips.total_bruto || '0',
                total_ajuste: datosRips.total_ajuste || '0',
                total_pagar: datosRips.total_pagar || '0'
            };
            setValoresTransaccion(recalcularTotalesTransaccion(iniciales));
        }
    }, [show, datosRips]);

    const recalcularTotalesTransaccion = (valores) => {
        const valorConsulta = parseFloat(valores.valorconsulta) || 0;
        const porcentajeDescuentoConsulta = parseFloat(valores.descuento_consulta) || 0;
        const ajusteConsulta = parseFloat(valores.ajuste_consulta) || 0;
        const porcentajeImpuestoConsulta = parseFloat(valores.impuesto_consulta) || 0;

        const valorProcedimiento = parseFloat(valores.valorprocedimiento) || 0;
        const porcentajeDescuentoProcedimiento = parseFloat(valores.descuento_procedimiento) || 0;
        const ajusteProcedimiento = parseFloat(valores.ajuste_procedimiento) || 0;
        const porcentajeImpuestoProcedimiento = parseFloat(valores.impuesto_procedimiento) || 0;

        const descuentoConsultaValor = (valorConsulta * porcentajeDescuentoConsulta) / 100;
        const descuentoProcedimientoValor = (valorProcedimiento * porcentajeDescuentoProcedimiento) / 100;

        const baseConsultaAjustada = valorConsulta - descuentoConsultaValor + ajusteConsulta;
        const baseProcedimientoAjustada = valorProcedimiento - descuentoProcedimientoValor + ajusteProcedimiento;

        const impuestoConsultaValor = (baseConsultaAjustada * porcentajeImpuestoConsulta) / 100;
        const impuestoProcedimientoValor = (baseProcedimientoAjustada * porcentajeImpuestoProcedimiento) / 100;

        const totalConsulta = baseConsultaAjustada + impuestoConsultaValor;
        const totalProcedimiento = baseProcedimientoAjustada + impuestoProcedimientoValor;

        return {
            ...valores,
            total_consulta: String(totalConsulta),
            total_procedimiento: String(totalProcedimiento),
            total_bruto: String(valorConsulta + valorProcedimiento),
            total_ajuste: String(ajusteConsulta + ajusteProcedimiento),
            total_pagar: String(totalConsulta + totalProcedimiento),
        };
    };

    const cargarCliente = async () => {
        try {
            const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
            const response = await axios.get('/get-client?uid=' + userdata.user_id, {
                headers: { Authorization: 'Bearer ' + userdata.access_token },
            });
            if (response.status === 200) {
                setClient(response.data);
            }
        } catch (error) {
            console.log('Error cargando cliente para sector salud:', error);
        }
    };

    const cargarConceptosFacturacion = async () => {
        try {
            const userdata = JSON.parse(localStorage.getItem('userdata') || '{}');
            const response = await axios.get('/conceptos-facturacion?uid=' + userdata.user_id, {
                headers: { Authorization: 'Bearer ' + userdata.access_token },
            });
            if (response.status === 200) {
                setConceptosFacturacion(response.data || []);
            }
        } catch (error) {
            console.log('Error cargando conceptos de facturación:', error);
            setConceptosFacturacion([]);
        }
    };

    const cargarParametrosFev = async () => {
        try {
            setLoading(true);
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            const config = {
                headers: { Authorization: 'Bearer ' + userdata.access_token }
            };

            const response = await axios.get('/parametros-fev?uid=' + userdata.user_id, config);
            
            if (response.status === 200) {
                let lista = Array.isArray(response.data) ? response.data : [];

                if (parametrosfevIdGuardado && !lista.some((p) => String(p.id) === String(parametrosfevIdGuardado))) {
                    try {
                        const extra = await axios.get('/parametros-fev/' + parametrosfevIdGuardado, config);
                        if (extra.status === 200 && extra.data) {
                            lista = [...lista, extra.data];
                        }
                    } catch (fetchExtra) {
                        console.warn('No se pudo cargar parametrosfev guardado:', fetchExtra);
                    }
                }

                setParametrosFev(lista);
            }
        } catch (error) {
            console.error('Error cargando parámetros FEV:', error);
            alert('Error al cargar los parámetros de facturación');
        } finally {
            setLoading(false);
        }
    };

    const esElectronica = (p) =>
        p.tipo === 'Factura Electronica' ||
        String(p.tipo || '').toLowerCase().includes('electr');

    const esParametroActivo = (p) =>
        p.estado == 1 || p.habilitado == 1;

    /**
     * Solo resoluciones del profesional del RIPS (users_id del registro).
     * Si no hay ninguna activa, fallback único: parametrosfev_id guardado en la factura.
     */
    const getPrefijosFiltrados = () => {
        let list = parametrosFev.filter(esElectronica);

        if (filtroPrefijoRelajado && list.length === 0) {
            list = parametrosFev.slice();
        }

        const porProfesionalRips = list.filter(
            (p) =>
                profesionalId &&
                String(p.users_id) === String(profesionalId) &&
                esParametroActivo(p)
        );

        if (porProfesionalRips.length) {
            return porProfesionalRips.sort((a, b) => a.id - b.id);
        }

        if (parametrosfevIdGuardado) {
            const guardado = parametrosFev.find(
                (p) => String(p.id) === String(parametrosfevIdGuardado) && esElectronica(p)
            );
            if (guardado) {
                return [guardado];
            }
        }

        return [];
    };

    React.useEffect(() => {
        if (!show || loading || parametrosFev.length === 0) return;

        const lista = getPrefijosFiltrados();
        if (!lista.length) return;

        const idGuardado = parametrosfevIdGuardado
            ? String(parametrosfevIdGuardado)
            : '';
        const idElegido =
            idGuardado && lista.some((p) => String(p.id) === idGuardado)
                ? idGuardado
                : String(lista[0].id);
        const selectedPrefijo = lista.find((p) => String(p.id) === idElegido);

        setDatosFacturacion((prev) => {
            if (prev.prefijo) return prev;
            return {
                ...prev,
                prefijo: idElegido,
                numerofactura: selectedPrefijo ? selectedPrefijo.numero_factura : '',
            };
        });
    }, [show, loading, parametrosFev, parametrosfevIdGuardado, profesionalId, filtroPrefijoRelajado]);

    const handlePrefijoChange = (e) => {
        const value = e.target.value;
        const selectedPrefijo = getPrefijosFiltrados().find(p => p.id == value);
        
        setDatosFacturacion(prev => ({
            ...prev,
            prefijo: value,
            numerofactura: selectedPrefijo ? selectedPrefijo.numero_factura : ''
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setDatosFacturacion(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleValorChange = (e) => {
        const { name, value } = e.target;
        const newValores = {
            ...valoresTransaccion,
            [name]: value
        };
        setValoresTransaccion(recalcularTotalesTransaccion(newValores));
    };

    const totalPagarNum = parseFloat(valoresTransaccion.total_pagar) || 0;
    const saldoPresupuestoNum = parseFloat(saldoDisponible) || 0;
    const excedeSaldoPresupuesto = !!(presupuestoSeleccionado && totalPagarNum > saldoPresupuestoNum + 0.009);

    const handleConfirmar = () => {
        // Validar que se haya seleccionado un prefijo
        if (!datosFacturacion.prefijo) {
            alert('Por favor seleccione un prefijo');
            return;
        }

        // Validar que se haya seleccionado un método de pago
        if (!datosFacturacion.metodo_pago) {
            alert('Por favor seleccione un método de pago');
            return;
        }

        if (excedeSaldoPresupuesto) {
            alert(
                `El valor de la factura ($${totalPagarNum.toLocaleString('es-CO', { minimumFractionDigits: 2 })}) ` +
                `excede el saldo disponible del presupuesto ($${saldoPresupuestoNum.toLocaleString('es-CO', { minimumFractionDigits: 2 })})`
            );
            return;
        }

        // Combinar datos de facturación con valores de transacción actualizados
        const datosCompletos = {
            ...datosFacturacion,
            ...valoresTransaccion,
            id_concepto_factura: datosFacturacion.id_concepto_factura
                ? parseInt(datosFacturacion.id_concepto_factura, 10)
                : null,
        };

        // Limpiar estado para forzar recarga en próxima apertura
        setParametrosFev([]);
        setDatosFacturacion({
            prefijo: '',
            numerofactura: '',
            metodo_pago: '10',
            forma_pago: 'contado',
            id_concepto_factura: '',
            contract_number: '',
            policy_number: ''
        });

        // Llamar al callback con los datos completos
        onConfirm(datosCompletos);
    };

    const handleCerrar = () => {
        // Resetear el formulario y parámetros para forzar recarga en próxima apertura
        setDatosFacturacion({
            prefijo: '',
            numerofactura: '',
            metodo_pago: '10',
            forma_pago: 'contado',
            id_concepto_factura: '',
            contract_number: '',
            policy_number: ''
        });
        setParametrosFev([]); // Limpiar para forzar recarga del consecutivo actualizado
        onClose();
    };

    return (
        <Modal show={show} onHide={handleCerrar} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fas fa-file-invoice mr-2"></i>
                    Datos de Facturación
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-info" role="status">
                            <span className="sr-only">Cargando...</span>
                        </div>
                        <p className="mt-2">Cargando parámetros de facturación...</p>
                    </div>
                ) : (
                    <>
                        <p className="text-muted mb-3">
                            Complete los siguientes datos para generar la factura electrónica:
                        </p>

                        {/* Información de valores de transacción - EDITABLES */}
                        {datosRips && (
                            <div className="card card-info card-outline mb-3">
                                <div className="card-header">
                                    <h6 className="mb-0"><i className="fas fa-calculator mr-2"></i>Valores de la Transacción</h6>
                                </div>
                                <div className="card-body">
                                    {/* Consulta */}
                                    {datosRips.tipo_consulta && (
                                        <div className="mb-3">
                                            <h6 className="font-weight-bold text-info">Consulta</h6>
                                            <div className="row">
                                                <div className="col-md-12 mb-2">
                                                    <small className="text-muted"><strong>Código CUPS:</strong> {datosRips.cupsc || 'N/A'}</small>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Valor Consulta ($)</label>
                                                        <input 
                                                            type="number" 
                                                            name="valorconsulta" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.valorconsulta}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Descuento (%)</label>
                                                        <input 
                                                            type="number" 
                                                            name="descuento_consulta" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.descuento_consulta}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Ajuste ($)</label>
                                                        <input 
                                                            type="number" 
                                                            name="ajuste_consulta" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.ajuste_consulta}
                                                            onChange={handleValorChange}
                                                            step="1"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Impuesto (%)</label>
                                                        <input 
                                                            type="number" 
                                                            name="impuesto_consulta" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.impuesto_consulta}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Total ($)</label>
                                                        <div className="bg-info text-white p-2 rounded text-center small font-weight-bold">
                                                            ${parseFloat(valoresTransaccion.total_consulta || 0).toLocaleString('es-CO')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Procedimiento */}
                                    {datosRips.tipo_procedimiento && (
                                        <div className="mb-3">
                                            <h6 className="font-weight-bold text-info">Procedimiento</h6>
                                            <div className="row">
                                                <div className="col-md-12 mb-2">
                                                    <small className="text-muted"><strong>Código CUPS:</strong> {datosRips.cupsp || 'N/A'}</small>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Valor Procedimiento ($)</label>
                                                        <input 
                                                            type="number" 
                                                            name="valorprocedimiento" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.valorprocedimiento}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Descuento (%)</label>
                                                        <input 
                                                            type="number" 
                                                            name="descuento_procedimiento" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.descuento_procedimiento}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Ajuste ($)</label>
                                                        <input 
                                                            type="number" 
                                                            name="ajuste_procedimiento" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.ajuste_procedimiento}
                                                            onChange={handleValorChange}
                                                            step="1"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Impuesto (%)</label>
                                                        <input 
                                                            type="number" 
                                                            name="impuesto_procedimiento" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.impuesto_procedimiento}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Total ($)</label>
                                                        <div className="bg-info text-white p-2 rounded text-center small font-weight-bold">
                                                            ${parseFloat(valoresTransaccion.total_procedimiento || 0).toLocaleString('es-CO')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Totales Generales */}
                                    <hr />
                                    <div className="row">
                                        <div className="col-4">
                                            <div className="form-group">
                                                <label className="font-weight-bold">Total Bruto</label>
                                                <div className="bg-light p-2 rounded text-center">
                                                    ${parseFloat(valoresTransaccion.total_bruto || 0).toLocaleString('es-CO')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="form-group">
                                                <label className="font-weight-bold">Ajuste Total</label>
                                                <div className="bg-light p-2 rounded text-center">
                                                    ${parseFloat(valoresTransaccion.total_ajuste || 0).toLocaleString('es-CO')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="form-group">
                                                <label className="font-weight-bold">Total a Pagar</label>
                                                <div className="bg-info text-white p-2 rounded font-weight-bold text-center">
                                                    ${parseFloat(valoresTransaccion.total_pagar || 0).toLocaleString('es-CO')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Sección de Presupuesto Activo - debajo de totales */}
                        {presupuestosActivos && presupuestosActivos.length > 0 && (
                            <div className="card card-success card-outline mb-3">
                                <div className="card-header py-2">
                                    <h6 className="mb-0"><i className="fas fa-wallet mr-2"></i>Descontar de Presupuesto Activo (opcional)</h6>
                                </div>
                                <div className="card-body py-2">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="form-group mb-2">
                                                <label className="small font-weight-bold">Seleccionar presupuesto</label>
                                                <select 
                                                    className="form-control form-control-sm"
                                                    value={presupuestoSeleccionado ? presupuestoSeleccionado.id : ''}
                                                    onChange={onSeleccionPresupuesto}
                                                >
                                                    <option value="">-- Sin presupuesto --</option>
                                                    {presupuestosActivos.map(p => (
                                                        <option key={p.id} value={p.id}>
                                                            #{p.numero_presupuesto} - Saldo: ${parseFloat(p.saldo_disponible).toLocaleString('es-CO')}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            {presupuestoSeleccionado && (
                                                <div className="form-group mb-2">
                                                    <label className="small font-weight-bold">Saldo disponible</label>
                                                    <div className={`p-2 rounded text-center font-weight-bold ${excedeSaldoPresupuesto ? 'bg-danger text-white' : 'bg-success text-white'}`}>
                                                        ${saldoPresupuestoNum.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    {excedeSaldoPresupuesto && (
                                        <div className="alert alert-danger py-2 mb-0 mt-2">
                                            <i className="fas fa-exclamation-triangle mr-1"></i>
                                            El total a pagar (${totalPagarNum.toLocaleString('es-CO', { minimumFractionDigits: 2 })}) supera el saldo del presupuesto (${saldoPresupuestoNum.toLocaleString('es-CO', { minimumFractionDigits: 2 })}). Ajuste los valores o elija otro presupuesto.
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="card card-secondary card-outline mb-3">
                            <div className="card-header py-2">
                                <h6 className="mb-0"><i className="fas fa-file-invoice mr-2"></i>Datos de la Factura</h6>
                            </div>
                            <div className="card-body py-2">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group mb-2">
                                            <label className="small font-weight-bold">Prefijo <span className="text-danger">*</span></label>
                                            <select 
                                                name="prefijo" 
                                                className="form-control form-control-sm" 
                                                value={datosFacturacion.prefijo} 
                                                onChange={handlePrefijoChange}
                                                required
                                            >
                                                <option value="">Seleccionar prefijo...</option>
                                                {getPrefijosFiltrados().map((p) => {
                                                    const etiquetaUsuario = [p.name, p.lastname].filter(Boolean).join(' ').trim();
                                                    return (
                                                    <option key={p.id} value={p.id}>
                                                        {p.prefijo} — {p.resolucion || etiquetaUsuario || 'Resolución FEV'}
                                                    </option>
                                                    );
                                                })}
                                            </select>
                                            {getPrefijosFiltrados().length === 0 && (
                                                <small className="form-text text-warning">
                                                    <i className="fas fa-exclamation-triangle mr-1"></i>
                                                    No hay resoluciones de facturación electrónica activas para el profesional de este RIPS
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group mb-2">
                                            <label className="small font-weight-bold">Número de Factura</label>
                                            <input 
                                                type="text" 
                                                name="numerofactura" 
                                                className="form-control form-control-sm bg-light" 
                                                value={datosFacturacion.numerofactura}
                                                readOnly
                                                placeholder="Se asignará automáticamente"
                                            />
                                            <small className="form-text text-muted">
                                                El número se asigna automáticamente según el prefijo seleccionado
                                            </small>
                                        </div>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="form-group mb-0">
                                            <label className="small font-weight-bold">Método de Pago <span className="text-danger">*</span></label>
                                            <select 
                                                name="metodo_pago" 
                                                className="form-control form-control-sm" 
                                                value={datosFacturacion.metodo_pago} 
                                                onChange={handleInputChange}
                                                required
                                            >
                                                {FORMA_PAGO_OPCIONES.map((op) => (
                                                    <option key={op.value} value={op.value}>{op.label}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group mb-0">
                                            <label className="small font-weight-bold">Concepto de factura</label>
                                            <select
                                                name="id_concepto_factura"
                                                className="form-control form-control-sm"
                                                value={datosFacturacion.id_concepto_factura}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Sin concepto</option>
                                                {conceptosFacturacion.map((concepto) => (
                                                    <option key={concepto.id} value={concepto.id}>{concepto.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-2">
                                    <div className="col-md-6">
                                        <div className="form-group mb-0">
                                            <label className="small font-weight-bold">N° de Contrato</label>
                                            <input
                                                type="text"
                                                name="contract_number"
                                                className="form-control form-control-sm"
                                                value={datosFacturacion.contract_number}
                                                onChange={handleInputChange}
                                                placeholder="Opcional - Sector salud"
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="form-group mb-0">
                                            <label className="small font-weight-bold">N° de Póliza</label>
                                            <input
                                                type="text"
                                                name="policy_number"
                                                className="form-control form-control-sm"
                                                value={datosFacturacion.policy_number}
                                                onChange={handleInputChange}
                                                placeholder="Opcional - Sector salud"
                                                maxLength={50}
                                            />
                                        </div>
                                    </div>
                                </div>
                                {esIps && (
                                    <div className="row mt-2">
                                        <div className="col-md-12">
                                            <small className="text-muted">
                                                {codigoPrestadorSalud ? (
                                                    <>
                                                        Código prestador IPS en FEV: <strong>{codigoPrestadorSalud}</strong>
                                                        {' '}(10 dígitos; mismo código en todas las facturas).
                                                    </>
                                                ) : (
                                                    <>Institución IPS sin código único: la factura se enviará sin extensión sector salud.</>
                                                )}
                                            </small>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="alert alert-info mt-3 mb-0">
                            <i className="fas fa-info-circle mr-2"></i>
                            <strong>Nota:</strong> Una vez confirmados estos datos, se generará la factura electrónica 
                            y podrá enviarla a la DIAN.
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCerrar}>
                    <i className="fas fa-times mr-1"></i>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirmar}
                    disabled={loading || getPrefijosFiltrados().length === 0 || !datosFacturacion.prefijo || excedeSaldoPresupuesto}
                >
                    <i className="fas fa-check mr-1"></i>
                    Confirmar y Facturar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalDatosFacturacion;
